import React from 'react';
import { useQuery } from 'react-query';
import { FormattedMessage, useIntl } from 'react-intl';

import apiRoutes from '../../setup/consts/apiRoutes';
import PublicShell from '../../components/PublicShell';
import { ComponentStatus, HealthResponse, ServiceHealth } from './types';
import {
  StyledStatusEyebrow,
  StyledStatusHero,
  StyledStatusHeroBody,
  StyledStatusHeroDot,
  StyledStatusHeroSub,
  StyledStatusHeroTitle,
  StyledStatusList,
  StyledStatusMeta,
  StyledStatusPill,
  StyledStatusRefresh,
  StyledStatusRow,
  StyledStatusRowName,
  StyledStatusSectionTitle,
  statusTone,
} from './styles';

const KNOWN_SERVICE_IDS = ['api', 'orders'];

const overallToComponent = (status?: HealthResponse['status']): ComponentStatus => {
  if (status === 'Degraded') return 'degraded';
  if (status === 'Unhealthy') return 'down';
  return 'operational';
};

const StatusPage: React.FC = () => {
  const intl = useIntl();

  const { data, isLoading, isError, isFetching, refetch, dataUpdatedAt } = useQuery<
    UseQueryResponse<HealthResponse>
  >(apiRoutes.SYSTEM_HEALTH, {
    refetchInterval: 30000,
    retry: 1,
  });

  const health = data?.response;

  // Resilient to the legacy `{ status: 'Healthy' }` shape (no `services`).
  const services: ServiceHealth[] =
    health?.services && health.services.length > 0
      ? health.services
      : [
          {
            key: 'api',
            name: 'Dashboard',
            status: overallToComponent(health?.status),
          },
        ];

  const heroTone = isError
    ? 'danger'
    : isLoading
    ? 'neutral'
    : statusTone(overallToComponent(health?.status));

  const heroTitleId = isError
    ? 'status.unreachable'
    : isLoading
    ? 'status.checking'
    : health?.status === 'Unhealthy'
    ? 'status.disruption'
    : health?.status === 'Degraded'
    ? 'status.degraded'
    : 'status.allOperational';

  const heroSubId = isError
    ? 'status.heroSubUnreachable'
    : isLoading
    ? 'status.heroSubChecking'
    : health?.status === 'Unhealthy'
    ? 'status.heroSubDisruption'
    : health?.status === 'Degraded'
    ? 'status.heroSubDegraded'
    : 'status.heroSubOperational';

  const serviceLabel = (service: ServiceHealth): string =>
    KNOWN_SERVICE_IDS.includes(service.key)
      ? intl.formatMessage({ id: `status.service.${service.key}`, defaultMessage: service.name })
      : service.name;

  const lastChecked = health?.timestamp
    ? new Date(health.timestamp)
    : dataUpdatedAt
    ? new Date(dataUpdatedAt)
    : undefined;

  return (
    <PublicShell seoTitle={intl.formatMessage({ id: 'status.eyebrow', defaultMessage: 'System status' })}>
      <StyledStatusEyebrow>
        <FormattedMessage id='status.eyebrow' defaultMessage='System status' />
      </StyledStatusEyebrow>

      <StyledStatusHero tone={heroTone}>
        <StyledStatusHeroDot tone={heroTone} />
        <StyledStatusHeroBody>
          <StyledStatusHeroTitle>
            <FormattedMessage id={heroTitleId} defaultMessage='All systems operational' />
          </StyledStatusHeroTitle>
          <StyledStatusHeroSub>
            <FormattedMessage
              id={heroSubId}
              defaultMessage='All Sandoog Center services are running normally.'
            />
          </StyledStatusHeroSub>
        </StyledStatusHeroBody>
      </StyledStatusHero>

      <StyledStatusSectionTitle>
        <FormattedMessage id='status.componentsTitle' defaultMessage='Components' />
      </StyledStatusSectionTitle>

      <StyledStatusList>
        {services.map((service) => {
          const tone = statusTone(service.status);
          return (
            <StyledStatusRow key={service.key}>
              <StyledStatusRowName>{serviceLabel(service)}</StyledStatusRowName>
              <StyledStatusPill tone={tone}>
                <FormattedMessage
                  id={`status.pill.${service.status}`}
                  defaultMessage={service.status}
                />
              </StyledStatusPill>
            </StyledStatusRow>
          );
        })}
      </StyledStatusList>

      <StyledStatusMeta>
        {lastChecked && (
          <span>
            <FormattedMessage
              id='status.lastChecked'
              defaultMessage='Last checked {time}'
              values={{ time: intl.formatTime(lastChecked, { hour: '2-digit', minute: '2-digit' }) }}
            />
          </span>
        )}
        <StyledStatusRefresh type='button' onClick={() => refetch()} disabled={isFetching}>
          <FormattedMessage
            id={isFetching ? 'status.refreshing' : 'status.refresh'}
            defaultMessage='Refresh'
          />
        </StyledStatusRefresh>
      </StyledStatusMeta>
    </PublicShell>
  );
};

export default StatusPage;
